/** Share actual in-flight work, with one trailing read when an event requires
 * fresher state. There is no settled cache: later reads still see revocation. */
export class CoalescedRequests {
  private generation = 0;
  private rows = new Map<string, { controller: AbortController; current: Promise<unknown>; next?: Promise<unknown> }>();

  run<T>(key: string, load: (signal: AbortSignal) => Promise<T>, fresh = false): Promise<T> {
    const active = this.rows.get(key);
    if (active) {
      if (!fresh) return (active.next ?? active.current) as Promise<T>;
      if (!active.next) {
        const generation = this.generation;
        active.next = active.current.catch(() => {}).then(() => {
          if (generation !== this.generation) throw new DOMException('Request superseded', 'AbortError');
          return this.start(key, load);
        });
      }
      return active.next as Promise<T>;
    }
    return this.start(key, load);
  }

  private start<T>(key: string, load: (signal: AbortSignal) => Promise<T>): Promise<T> {
    const controller = new AbortController();
    let current:Promise<T>;
    try { current=load(controller.signal); } catch (error) { current=Promise.reject(error); }
    const row: { controller: AbortController; current: Promise<T>; next?: Promise<unknown> } = { controller, current };
    this.rows.set(key, row);
    void row.current.finally(() => { if (this.rows.get(key) === row && !row.next) this.rows.delete(key); }).catch(() => {});
    return row.current;
  }

  invalidate() {
    this.generation++;
    for (const row of this.rows.values()) row.controller.abort();
    this.rows.clear();
  }
}
